import { IInputProps } from 'modules';
import React, { FC, useMemo, useState } from 'react';
import { Icon } from 'shared/ui/Icon';

export interface GroupedCheckboxOption {
  label: string;
  value: string;
}

interface ScreenActionPermissionGroup {
  key: string;
  label: string;
  options: GroupedCheckboxOption[];
}

interface InputGroupedCheckboxProps extends IInputProps {
  options?: GroupedCheckboxOption[];
  onResetAgency?: () => void;
}

const screenActionSeparator = /\s+-\s+/;

const getScreenActionGroupName = (option: GroupedCheckboxOption) => {
  const labelParts = option.label.split(screenActionSeparator);
  if (labelParts.length > 1 && labelParts[0]?.trim()) {
    return labelParts[0].trim();
  }

  const [keyGroup] = option.value.split('_');
  return keyGroup || 'Khác';
};

const getScreenActionLabel = (option: GroupedCheckboxOption) => {
  const labelParts = option.label.split(screenActionSeparator);
  if (labelParts.length > 1) {
    return labelParts.slice(1).join(' - ').trim();
  }

  return option.label;
};

const groupScreenActionPermissions = (options: GroupedCheckboxOption[]) =>
  options.reduce<ScreenActionPermissionGroup[]>((groups, option) => {
    const groupLabel = getScreenActionGroupName(option);
    const groupKey = groupLabel.toLowerCase();
    const existedGroup = groups.find((group) => group.key === groupKey);

    if (existedGroup) {
      existedGroup.options.push(option);
      return groups;
    }

    groups.push({
      key: groupKey,
      label: groupLabel,
      options: [option],
    });

    return groups;
  }, []);

const defaultOptions: GroupedCheckboxOption[] = [];

export const normalizeGroupedCheckboxValues = (
  value: any,
  options: GroupedCheckboxOption[],
) => {
  const optionValues = new Set(options.map((option) => option.value));

  return Array.from(
    new Set(
      (Array.isArray(value) ? value : []).filter((selectedValue) =>
        optionValues.has(selectedValue),
      ),
    ),
  );
};

const PermissionCheckbox = ({
  checked,
  label,
  name,
  onChange,
}: {
  checked: boolean;
  label: string;
  name: string;
  onChange: () => void;
}) => (
  <label
    className='InputGroupedCheckbox__checkbox'
    onClick={(e) => {
      e.preventDefault();
      onChange();
    }}
  >
    <input
      readOnly
      checked={checked}
      name={name}
      type='checkbox'
      tabIndex={-1}
    />
    <span className='InputGroupedCheckbox__checkboxIcon' />
    <span className='InputGroupedCheckbox__checkboxLabel'>{label}</span>
  </label>
);

export const InputGroupedCheckbox: FC<InputGroupedCheckboxProps> = (props) => {
  const [searchValue, setSearchValue] = useState('');
  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<string[]>([]);
  const options = props.options || defaultOptions;
  const selectedActions = normalizeGroupedCheckboxValues(props.value, options);
  const searchKeyword = searchValue.trim().toLowerCase();

  const filteredOptions = useMemo(
    () =>
      options.filter((option) => {
        const groupName = getScreenActionGroupName(option);

        return `${groupName} ${option.label} ${option.value}`
          .toLowerCase()
          .includes(searchKeyword);
      }),
    [options, searchKeyword],
  );

  const actionGroups = useMemo(
    () => groupScreenActionPermissions(filteredOptions),
    [filteredOptions],
  );

  const visibleActionValues = filteredOptions.map((option) => option.value);
  const isAllVisibleSelected =
    visibleActionValues.length > 0 &&
    visibleActionValues.every((value) => selectedActions.includes(value));
  const visibleGroupKeys = actionGroups.map((group) => group.key);
  const isAllGroupsCollapsed =
    visibleGroupKeys.length > 0 &&
    visibleGroupKeys.every((key) => collapsedGroupKeys.includes(key));

  const handleChange = (nextActions: string[]) => {
    props.onResetAgency?.();
    props.onChange(nextActions);
  };

  const toggleActions = (actionValues: string[]) => {
    const isAllSelected =
      actionValues.length > 0 &&
      actionValues.every((value) => selectedActions.includes(value));

    handleChange(
      isAllSelected
        ? selectedActions.filter((value) => !actionValues.includes(value))
        : Array.from(new Set([...selectedActions, ...actionValues])),
    );
  };

  const toggleAllGroups = () => {
    setCollapsedGroupKeys(isAllGroupsCollapsed ? [] : visibleGroupKeys);
  };

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroupKeys((current) =>
      current.includes(groupKey)
        ? current.filter((key) => key !== groupKey)
        : [...current, groupKey],
    );
  };

  return (
    <div className='InputGroupedCheckbox'>
      <div className='InputGroupedCheckbox__toolbar'>
        <input
          type='text'
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder='Tìm kiếm'
          className='InputGroupedCheckbox__search'
        />
        <div className='InputGroupedCheckbox__actions'>
          <button
            type='button'
            className='InputGroupedCheckbox__toggle'
            onClick={() => toggleActions(visibleActionValues)}
            disabled={visibleActionValues.length === 0}
          >
            {isAllVisibleSelected ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
          </button>
          <button
            type='button'
            className='InputGroupedCheckbox__toggle InputGroupedCheckbox__expand'
            onClick={toggleAllGroups}
            disabled={actionGroups.length === 0}
          >
            {isAllGroupsCollapsed ? (
              <Icon.Down
                size={14}
                className='InputGroupedCheckbox__expandIcon'
              />
            ) : (
              <Icon.Up size={14} className='InputGroupedCheckbox__expandIcon' />
            )}
            {isAllGroupsCollapsed ? 'Mở rộng' : 'Thu gọn'}
          </button>
        </div>
      </div>

      <div className='InputGroupedCheckbox__summary'>
        Đã chọn {selectedActions.length}/{options.length} thao tác
      </div>

      <div className='InputGroupedCheckbox__groups'>
        {actionGroups.length > 0 ? (
          actionGroups.map((group) => {
            const groupActionValues = group.options.map(
              (option) => option.value,
            );
            const selectedGroupCount = groupActionValues.filter((value) =>
              selectedActions.includes(value),
            ).length;
            const isGroupChecked =
              groupActionValues.length > 0 &&
              selectedGroupCount === groupActionValues.length;
            const isGroupCollapsed = collapsedGroupKeys.includes(group.key);

            return (
              <div className='InputGroupedCheckbox__group' key={group.key}>
                <div className='InputGroupedCheckbox__groupHeader'>
                  <PermissionCheckbox
                    checked={isGroupChecked}
                    name={group.key}
                    label={group.label}
                    onChange={() => {
                      toggleActions(groupActionValues);
                      props.onTouched?.();
                    }}
                  />
                  <div className='InputGroupedCheckbox__groupMeta'>
                    <div className='InputGroupedCheckbox__groupCount'>
                      {selectedGroupCount}/{group.options.length}
                    </div>
                    <button
                      type='button'
                      className='InputGroupedCheckbox__groupToggle'
                      onClick={() => toggleGroup(group.key)}
                      aria-label={
                        isGroupCollapsed
                          ? `Mở rộng ${group.label}`
                          : `Thu gọn ${group.label}`
                      }
                    >
                      {isGroupCollapsed ? (
                        <Icon.Down
                          size={14}
                          className='InputGroupedCheckbox__groupToggleIcon'
                        />
                      ) : (
                        <Icon.Up
                          size={14}
                          className='InputGroupedCheckbox__groupToggleIcon'
                        />
                      )}
                    </button>
                  </div>
                </div>

                {!isGroupCollapsed && (
                  <div className='InputGroupedCheckbox__groupItems'>
                    {group.options.map((option) => {
                      const isChecked = selectedActions.includes(option.value);

                      return (
                        <PermissionCheckbox
                          checked={isChecked}
                          key={option.value}
                          name={option.value}
                          label={getScreenActionLabel(option)}
                          onChange={() => {
                            handleChange(
                              isChecked
                                ? selectedActions.filter(
                                    (value) => value !== option.value,
                                  )
                                : [...selectedActions, option.value],
                            );
                            props.onTouched?.();
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className='InputGroupedCheckbox__empty'>
            Không có thao tác phù hợp
          </div>
        )}
      </div>
    </div>
  );
};
